import React, { useEffect, useRef, useState } from 'react';

interface Stat {
  value: number;
  suffix: string;
  label: string;
}

const stats: Stat[] = [
  { value: 5,   suffix: '',  label: 'Integrated modules covering every ESG obligation' },
  { value: 2,   suffix: '',  label: 'Regional frameworks — India BRSR & EU regulatory stack' },
  { value: 15,  suffix: '+', label: 'GHG Protocol Scope 3 categories supported' },
  { value: 100, suffix: '%', label: 'Audit-ready evidence trail on every data point' },
];

interface CounterProps {
  value: number;
  suffix: string;
}

function Counter({ value, suffix }: CounterProps): React.ReactElement {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const observed = useRef(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) { setCount(value); return; }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !observed.current) {
          observed.current = true;
          const duration = 1600;
          let start: number | null = null;

          const step = (ts: number): void => {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * value));
            if (progress < 1) requestAnimationFrame(step);
            else setCount(value);
          };

          requestAnimationFrame(step);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span className="stat-number font-mono" ref={ref}>
      {count}{suffix}
    </span>
  );
}

export default function StatsBar(): React.ReactElement {
  return (
    <section className="stats" id="stats">
      <div className="container">
        <div className="stats-grid">
          {stats.map((s, i) => (
            <div key={i} className="stat-item">
              <Counter value={s.value} suffix={s.suffix} />
              <p className="stat-label">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
